/*
Copyright© Celligence International, LLC - 2024
This software program is protected by copyright laws and is the property of
Celligence International, LLC (Celligence). Any unauthorized reproduction,
distribution, modification, or public display of this program, without the
explicit permission of Celligence is strictly prohibited. If you have gained
access to this program without the expressed approval of Celligence report
the incident to legal@celligence.com.
*/

import { Button } from '@mui/material';
import Styles from '../../css/BorrowerEscrowAnalysis.module.css';
import useDocumentsRecognize from '../../common/hooks/useDocumentsRecognize';
import { useCallback, useEffect } from 'react';
import useGetImagingDoc from '../../common/hooks/useGetImagingDocument';
import { isObjEmptyOrNull } from '../../common/UtilityFunctions';
import LoadingModal from '../modals/LoadingModal';
import { useBorrowerEscrowAnalysisContext } from '../../contexts/BorrowerEscrowAnalysisContext';

const BorrowerEscrowAnalysis = () => {
  const {
    sortedAEADocs,
    loading: documentsDataLoading,
    errors: documentsDataErrors,
  } = useBorrowerEscrowAnalysisContext();

  const { loading: imagingDocLoading, errors: imagingDocErrors, getImagingDoc } = useGetImagingDoc();

  const fetchImagingDoc = useCallback(async (imagingDoc: Blob | null) => {
    if (!isObjEmptyOrNull(imagingDoc)) {
      const objectURL = window.URL.createObjectURL(imagingDoc); // Create temp URL for blob
      window.open(objectURL, '_blank');
    }
  }, []);

  const handleLoad = useCallback(
    async (current: boolean) => {
      const docIdArrIndex = current ? sortedAEADocs.length - 1 : sortedAEADocs.length - 2;
      const imagingDoc = await getImagingDoc(sortedAEADocs[docIdArrIndex].documentId.toString());
      await fetchImagingDoc(imagingDoc);
    },
    [sortedAEADocs, getImagingDoc, fetchImagingDoc],
  );

  var errors = documentsDataErrors !== '' ? documentsDataErrors : '';
  errors =
    errors + (imagingDocErrors !== '' ? 'There was a problem downloading your escrow analysis. Please try again.' : '');

  return (
    <div className={Styles.formContent}>
      {(documentsDataLoading || imagingDocLoading) && <LoadingModal open={true} />}

      {(documentsDataErrors !== '' || imagingDocErrors !== '') && <p className={Styles.text}>{errors}</p>}

      {sortedAEADocs.length != 0 && (
        <p className={Styles.textBlack}>Choose one of the following options to view your escrow analysis:</p>
      )}

      {sortedAEADocs.length == 0 && <p className={Styles.textBlack}>No escrow analysis documents were found.</p>}

      <div className={Styles.buttonrow}>
        {sortedAEADocs.length > 1 && (
          <div className={Styles.button}>
            <Button
              className={Styles.secondarybutton}
              variant='outlined'
              size='large'
              onClick={() => {
                handleLoad(false);
              }}
            >
              Previous
            </Button>
          </div>
        )}

        {sortedAEADocs.length > 0 && (
          <div className={Styles.button}>
            <Button
              variant='outlined'
              size='large'
              className={Styles.primbutton}
              disabled={sortedAEADocs.length == 0}
              onClick={() => {
                handleLoad(true);
              }}
            >
              Current
            </Button>
          </div>
        )}
      </div>

      <div className={Styles.linkswrapper}>
        <div className={Styles.link}>
          <a target='_blank' rel='noopener noreferrer' href={process.env.REACT_APP_HOWTO_READAEA_ENGLISH}>
            How to Read Annual Escrow Analysis Letter (English)
          </a>
        </div>

        <div className={Styles.link}>
          <a target='_blank' rel='noopener noreferrer' href={process.env.REACT_APP_HOWTO_READAEA_SPANISH}>
            Como leer su Estado de Divulgación Anual de la Cuenta de Reserva (Spanish)
          </a>
        </div>
      </div>
    </div>
  );
};

export default BorrowerEscrowAnalysis;
